/**
 * backHandlerStack — Centralized Android back button handler.
 *
 * Priority-based callback stack (same pattern as Android's OnBackPressedDispatcher).
 * UI layers register/unregister handlers; the highest-priority active handler
 * runs on each back event. An observer layer runs on every event (e.g., force-save).
 *
 * Only active on native platforms (Capacitor Android). No-op on web/iOS.
 *
 * @see docs/plans/view-and-dnd-master-plan.md §51
 */

import { Capacitor } from "@capacitor/core"
import { addBreadcrumb } from "@/shared/telemetry/breadcrumbs"

// ---------------------------------------------------------------------------
// Priority constants
// ---------------------------------------------------------------------------

export const BACK_PRIORITY = {
  DIALOG: 100,
  SHEET: 90,
  SEARCH: 80,
  TASK_DETAIL: 70,
  SIDEBAR: 60,
  ROOT_SCREEN: 50,
  FALLBACK: 0,
} as const

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type UnregisterFn = () => void

interface HandlerEntry {
  id: number
  priority: number
  handler: () => boolean | void
  label?: string
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

let nextId = 0
const handlers: HandlerEntry[] = []
const observers: Array<{ id: number; handler: () => void }> = []
let listenerHandle: { remove: () => Promise<void> | void } | null = null
let initialized = false

function sortHandlers() {
  handlers.sort((a, b) => b.priority - a.priority)
}

/**
 * Handle a back button press. Runs observers first, then the highest-priority handler.
 */
function handleBack() {
  // Always run all observers (force-save, telemetry, etc.)
  for (const obs of observers) {
    try {
      obs.handler()
    } catch {
      // observers must not throw
    }
  }

  // Find first active handler (highest priority, already sorted)
  for (const entry of handlers) {
    try {
      const consumed = entry.handler()
      addBreadcrumb({
        category: "navigation",
        message: `[Back] ${entry.label ?? `p${entry.priority}`}`,
        data: { priority: entry.priority },
      })
      if (consumed !== false) return // handler consumed the event
    } catch {
      // handler threw — skip to next
    }
  }

  // No handler consumed → default browser back
  window.history.back()
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const backHandlerStack = {
  /**
   * Register a back handler. Higher priority runs first.
   * Handler should return `true` (or void) if it consumed the event.
   * Return `false` to pass to the next handler.
   */
  register(
    priority: number,
    handler: () => boolean | void,
    label?: string,
  ): UnregisterFn {
    const id = nextId++
    handlers.push({ id, priority, handler, label })
    sortHandlers()
    return () => {
      const idx = handlers.findIndex((h) => h.id === id)
      if (idx !== -1) handlers.splice(idx, 1)
    }
  },

  /**
   * Register an observer that runs on EVERY back event, regardless of
   * which handler consumes it. Use for force-save, telemetry, etc.
   */
  registerObserver(handler: () => void): UnregisterFn {
    const id = nextId++
    observers.push({ id, handler })
    return () => {
      const idx = observers.findIndex((o) => o.id === id)
      if (idx !== -1) observers.splice(idx, 1)
    }
  },

  /**
   * Start listening to Capacitor App backButton event.
   * Call once in AppLayout. Idempotent.
   */
  async init(): Promise<void> {
    if (initialized) return
    if (!Capacitor.isNativePlatform()) return
    if (Capacitor.getPlatform() !== "android") return

    initialized = true

    try {
      const { App } = await import("@capacitor/app")
      listenerHandle = await App.addListener("backButton", () => {
        handleBack()
      })
    } catch {
      // @capacitor/app not available
      initialized = false
    }
  },

  /** Stop listening. */
  destroy(): void {
    listenerHandle?.remove()
    listenerHandle = null
    initialized = false
  },

  /** Number of registered handlers (for debugging). */
  get size(): number {
    return handlers.length
  },
}
